import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routeConfiguration';
import {
  isStartup,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  propTypes,
} from '../../util/types';
import {
  Avatar,
  InlineTextButton,
  Logo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  ListingLink,
  OwnListingLink,
} from '../../components';
import { TopbarSearchForm } from '../../forms';
import SubscriptionReminderModal from './SubscriptionReminderModal';
import {
  createSlug,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';

import MenuIcon from './MenuIcon';

import css from './TopbarDesktop.module.css';

const TopbarDesktop = props => {
  const {
    className,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    currentUserListing,
    currentUserListingFetched,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    onHandleSubscriptionPopup,
    history,
    providerType
  } = props;
  const [mounted, setMounted] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);
  const isProvider = currentUser?.attributes?.profile?.publicData?.userType === 'service-provider';
  const offerEnabled = !!currentUser?.attributes?.profile?.publicData?.offerEnabled;
  const profileCompleted = !!currentUser?.attributes?.profile?.publicData?.profileCompleted;

  const search = (
    <TopbarSearchForm
      className={css.searchLink}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      // initialValues={initialSearchFormValues}
      isProvider={isProvider}
      providerType={providerType}
    />
  );

  const subscriptions = currentUser?.attributes?.profile?.privateData?.subscriptions || [];
  const hasAnySubscriptions = subscriptions.length > 0;

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={classNames(css.inboxLink, css.desktopLink)}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };


  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  const logOutLink = isAuthenticatedOrJustHydrated && (
    <span className={css.loginLink} onClick={onLogout}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.logout" />
      </span>
    </span>
  );

  const listingLink =
    authenticatedOnClientSide && currentUserListingFetched && currentUserListing ? (
      <ListingLink
        className={css.createListingLink}
        listing={currentUserListing}
        children={
          <span className={css.createListing}>
            <FormattedMessage id="TopbarDesktop.viewListing" />
          </span>
        }
      />
    ) : null;

  const createListingLink =
    (isAuthenticatedOrJustHydrated && !(currentUserListingFetched && !currentUserListing)) ||
      !isProvider ? null : hasAnySubscriptions ? (
        <NamedLink className={css.createListingLink} name="NewListingPage">
          <span className={css.createListing}>
            <FormattedMessage id="TopbarDesktop.createListing" />
          </span>
        </NamedLink>
      ) : (
      <span className={css.createListingLink} onClick={() => setSubscriptionModalOpen(true)}>
        <span className={css.createListing}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </span>
    );

  const contactUsLink = isAuthenticatedOrJustHydrated && (
    <NamedLink className={classNames(css.createListingLink, css.desktopLink)} name="ContactPage">
      <span className={css.createListing}>Contact Us</span>
    </NamedLink>
  );

  const id = currentUserListing?.id?.uuid;
  const { title, state } = currentUserListing?.attributes || {};
  const slug = title && createSlug(title);
  const isPendingApproval = state === LISTING_STATE_PENDING_APPROVAL;
  const isDraft = state === LISTING_STATE_DRAFT;
  const variant = isPendingApproval
    ? LISTING_PAGE_PENDING_APPROVAL_VARIANT
    : isDraft
      ? LISTING_PAGE_DRAFT_VARIANT
      : null;

  const ownListingLinkProps = !!variant
    ? {
      name: 'ListingPageVariant',
      params: {
        id,
        slug,
        variant,
      },
    }
    : {
      name: 'ListingPage',
      params: { id, slug },
    };


  const hasProfileCreated = slug && id;

  const profileLink =
    isAuthenticatedOrJustHydrated &&
    (hasProfileCreated ? (
      <NamedLink className={css.createListingLink} {...ownListingLinkProps}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </NamedLink>
    ) : (
      <NamedLink className={css.createListingLink} name={'NewListingPage'}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </NamedLink>
    ));

  const becomeProviderLink =
    isAuthenticatedOrJustHydrated && !isProvider ? (
      <span
        className={classNames(css.createListingLink, css.desktopLink)}
        onClick={onHandleSubscriptionPopup}
      >
        <span className={css.createListing}>Become a provider</span>
      </span>
    ) : null;

  const referAcontactButton =
    <NamedLink className={css.referAcontactButton} name="ReferAContactPage">
      <FormattedMessage id="TopbarDesktop.referAcontact" />
    </NamedLink>

  const reviewsButton = (
    <NamedLink className={css.referAcontactButton} name="CollectReviewPage">
      {isStartup(currentUser) ?
        <FormattedMessage id="TopbarDesktop.votes" /> :
        <FormattedMessage id="TopbarDesktop.reviews" />
      }
    </NamedLink>
  )

  const offersLink = authenticatedOnClientSide && offerEnabled && (
    <NamedLink
      className={classNames(css.createListingLink, css.desktopLink)}
      name="ManageOffersPage"
    >
      <span className={css.createListing}>
        <FormattedMessage id="TopbarDesktop.offers" />
      </span>
    </NamedLink>
  );

  const tabletMenu = (
    <Menu>
      <MenuLabel className={css.tabletMenuLabel} isOpenClassName={css.tabletMenuLabelIsOpen}>
        Menu
        <MenuIcon />
      </MenuLabel>
      <MenuContent className={css.tabletMenuContent}>

        <MenuItem key="reviewsButton">
            <NamedLink className={classNames(css.createListingLink, css.desktopLink)} name="CollectReviewPage">
              <span className={css.createListing}>
                <FormattedMessage id="TopbarDesktop.reviews" />
              </span>
            </NamedLink>
        </MenuItem>

        <MenuItem key="referAcontactButton">
          <NamedLink className={classNames(css.createListingLink, css.desktopLink)} name="ReferAContactPage">
            <span className={css.createListing}>
              <FormattedMessage id="TopbarDesktop.referAcontact" />
            </span>
          </NamedLink>
        </MenuItem>

        <MenuItem key="contactUsLink">
          {contactUsLink}
        </MenuItem>
        <MenuItem key="becomeProviderLink">
          {becomeProviderLink}
        </MenuItem>
        <MenuItem key="inboxLink">
          {inboxLink}
        </MenuItem>
        <MenuItem key="offersLink">
          {offersLink}
        </MenuItem>
      </MenuContent>
    </Menu>
  )

  return (
    <nav className={classes}>
      <NamedLink className={css.logoLink} name="LandingPage">
        <Logo
          format="desktop"
          className={css.logo}
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
        />
      </NamedLink>
      {search}
      {reviewsButton}
      {referAcontactButton}
      {contactUsLink}
      {becomeProviderLink}
      {inboxLink}
      {offersLink}

      {tabletMenu}

      {profileLink}
      {logOutLink}
      {signupLink}
      {loginLink}
      <SubscriptionReminderModal
        isOpen={subscriptionModalOpen}
        setOpen={setSubscriptionModalOpen}
        history={history}
      />
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  currentUserListing: null,
  currentUserListingFetched: false,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUserListing: propTypes.ownListing,
  currentUserListingFetched: bool,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
};

export default TopbarDesktop;
